import { 
  useState, 
  useCallback, 
  useMemo,
  useEffect,
  useRef
} from "react";

import { 
  getOtbVersionDraft,
  saveOtbVersionDraft,
  deleteOtbVersionDraft
} from '../../../database/otbVersionDraftDB'

import { buildRawMonthlySeries } from "../business_rules/aggregation";
import { applyEditsToRawData } from "../business_rules/applyEditsToRawData";
import { applyOtbEdit, resolveRecomputeFrom } from "../business_rules/otb";

// const COVERAGE_TARGET = 3;
const SAVE_DEBOUNCE_MS = 800;

const DEFAULT_COVERAGE_TARGET = 3;

const resolveCoverageTarget = (data) => {
  const found = (data?.estoquePecasData ?? [])
    .map((d) => Number(d.plannedInventoryCoverage))
    .find((n) => Number.isFinite(n) && n > 0);
  return found ?? DEFAULT_COVERAGE_TARGET;
};

const getCurrentMonthKey = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
};

export function useOtbCategoryEdits(categoriaData, displayCategory, { tenantId, userId, cycleId } = {}) {
  const [editedByCategory, setEditedByCategory] = useState({});
  const [editedAnchors, setEditedAnchors] = useState({});
  const [draftJustHydrated, setDraftJustHydrated] = useState(false);
  const saveTimer = useRef(null);
  const hydratedCycle = useRef(null);

  useEffect(() => {

    if (!tenantId || !userId || !cycleId) return;

    if (hydratedCycle.current === cycleId) return;

    hydratedCycle.current = cycleId;

    getOtbVersionDraft({
      tenantId,
      userId,
      cycleId,
    }).then((draft) => {
      if (draft?.editedByCategory && Object.keys(draft.editedByCategory).length > 0) {
        setEditedByCategory(draft.editedByCategory);
        setEditedAnchors(draft.editedAnchors ?? {});
        setDraftJustHydrated(true);
      } else {
        setEditedByCategory({});
        setEditedAnchors({});
        setDraftJustHydrated(false);
      }
    });
  }, [tenantId, userId, cycleId]);

  useEffect(() => {
    if (!tenantId || !userId || !cycleId) return;
    if (Object.keys(editedByCategory).length === 0) return;

    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      saveOtbVersionDraft({ tenantId, userId, cycleId, editedByCategory, editedAnchors });
    }, SAVE_DEBOUNCE_MS);

    return () => clearTimeout(saveTimer.current);
  }, [editedByCategory, editedAnchors, tenantId, userId, cycleId]);

  const editedMonths = editedByCategory[displayCategory] ?? {};

  const effectiveData = useMemo(
    () => applyEditsToRawData(categoriaData, editedMonths),
    [categoriaData, editedMonths]
  );

  const handleFieldEdit = useCallback((editedField, anchorIndex, rawInputValue) => {
    const newValue = parseFloat(rawInputValue);
    if (isNaN(newValue)) return;

    const monthsData = buildRawMonthlySeries(effectiveData);
    const coverageTarget = resolveCoverageTarget(effectiveData);
    const result = applyOtbEdit({ monthsData, anchorIndex, editedField, newValue, coverageTarget });

    const recomputeFrom = resolveRecomputeFrom({ monthsData, anchorIndex, coverageTarget });
    const currentMonthKey = getCurrentMonthKey();

    setEditedByCategory((prev) => {
      const prevForCategory = prev[displayCategory] ?? {};
      const nextForCategory = { ...prevForCategory };
      result.forEach((month, i) => {
        if (i < recomputeFrom) return;
        const key = `${month.year}-${String(month.month).padStart(2, '0')}`;
        nextForCategory[key] = {
          ...month,
          editedBase: Boolean(month.editedBase || prevForCategory[key]?.editedBase),
        };
      });
      return { ...prev, [displayCategory]: nextForCategory };
    });

    setEditedAnchors((prev) => {
      const prevForCategory = prev[displayCategory] || {};
      const nextForCategory = { ...prevForCategory };
      result.forEach((month, i) => {
        if (i < recomputeFrom) return;
        const key = `${month.year}-${String(month.month).padStart(2, '0')}`;
        if (month.editedBase && key >= currentMonthKey) {
          nextForCategory[key] = true;
        }
      });
      return { ...prev, [displayCategory]: nextForCategory };
    });
  }, [effectiveData, displayCategory]);

  const resetCategoryEdits = useCallback(() => {
    setEditedByCategory((prev) => {
      if (!prev[displayCategory]) return prev;
      const { [displayCategory]: _removed, ...rest } = prev;
      return rest;
    });
    setEditedAnchors((prev) => {
      if (!prev[displayCategory]) return prev;
      const { [displayCategory]: _removed, ...rest } = prev;
      return rest;
    });
  }, [displayCategory]);

  const resetAllEdits = useCallback(() => {
    setEditedByCategory({});
    setEditedAnchors({});
    if (tenantId && userId && cycleId) {
      deleteOtbVersionDraft({ tenantId, userId, cycleId });
    }
  }, [tenantId, userId, cycleId]);

  const acknowledgeDraft = useCallback(() => setDraftJustHydrated(false), []);

  return {
    effectiveData,
    handleFieldEdit,
    resetCategoryEdits,
    resetAllEdits,
    editedByCategory,
    editedAnchors,
    draftJustHydrated,
    acknowledgeDraft,
  };
}